import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useAudienceCopy } from '../../../hooks/useAudienceCopy';
import '../../../styles/aida-base.css';
import '../../../styles/aida-responsive.css';

/**
 * HostThreePlatform Component
 * Three alternating info sections explaining the host value proposition:
 *   1. How Playcast Brings Games to the World
 *   2. Host When You're Away From Your PC
 *   3. Earn Rewards the Longer You Host
 * Desktop: absolute-positioned 1024px canvas (hidden lg:block)
 * Mobile: vertically stacked section cards (flex lg:hidden)
 * Transparent background — inherits from page background zones.
 */

const WHITE = 'rgba(255, 255, 255, 1)';
const YELLOW = 'rgba(251, 191, 36, 1)';

const titleStyle: React.CSSProperties = {
  lineHeight: '25px',
  fontFamily: 'Inter',
  fontWeight: 400,
  fontSize: '25px',
  margin: 0,
};

const titleSpanStyle: React.CSSProperties = {
  lineHeight: '25px',
  fontFamily: 'Inter',
  fontWeight: 400,
  color: WHITE,
  fontStyle: 'normal',
  fontVariationSettings: "'wght' 900",
};

const bodyStyle: React.CSSProperties = {
  lineHeight: '13px',
  fontFamily: 'Inter',
  fontWeight: 400,
  fontSize: '10px',
  margin: 0,
};

const bodySpanStyle: React.CSSProperties = {
  lineHeight: '13px',
  fontFamily: 'Inter',
  fontWeight: 400,
  color: WHITE,
  fontStyle: 'normal',
  fontVariationSettings: "'wght' 350",
};

const SECTION_BG = 'linear-gradient(180deg, #29334e, #4a5580)';

// Mobile card 1 image constants
const MOB_S1_GIBBER = 'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-9724e1db-3997-456e-9e88-84e86d7f7b6a.png?w=472&cX=1221&cY=100&cW=694&cH=800';
const MOB_S1_DECOR = 'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-2990fe30-8f7b-4049-aad1-6f7c843aabd7.png?w=423&cX=0&cY=1384&cW=1168&cH=699';
const MOB_S1_INNER_CARD = 'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-c669ceb4-69ee-41bf-8421-10a2503b8ea0.png?w=904&cX=231.15674242796132&cY=0&cW=737.6865151440774&cH=628';

// Mobile card 2 image constants
const MOB_S2_GIBBER = 'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-ad20fdd9-2af8-499f-aa9d-314d3797bb9d.png?w=383&cX=688&cY=233&cW=563&cH=670';
const MOB_S2_INNER_CARD = 'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-4cd56917-22b2-4984-871d-6b69c43c2003.png?w=888&cX=282.13147238735246&cY=0&cW=635.7370552252951&cH=628';

// Mobile card 3 image constants
const MOB_S3_GIBBER = 'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-552c97b8-f821-43a5-a3ee-951584c669a5.png?w=384&cX=86&cY=165&cW=596&cH=722';
const MOB_S3_DECOR = 'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-2990fe30-8f7b-4049-aad1-6f7c843aabd7.png?w=423&cX=0&cY=1384&cW=1168&cH=699';
const MOB_S3_INNER_CARD = 'https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-fe64a109-7b05-4a14-82ff-3b4370452e1a.png?w=900&cX=260.3408585055644&cY=0&cW=679.3182829888711&cH=628';

export const HostThreePlatform = () => {
  const desktopRef = useRef(null);
  const mobileRef = useRef(null);
  const isInView = useInView(desktopRef, { once: true, margin: '-100px' });
  const isMobileInView = useInView(mobileRef, { once: true, amount: 0.1 });

  const section3Body = useAudienceCopy(
    'hostthreeplatform-s3-body',
    'While powering the Playcast network, earn tokens and unlock rewards. As the network grows, so does the reward pool: new games, new perks, and exclusive community events.',
  );

  return (
    <>
      {/* ── Desktop Layout ── */}
      {/* All top values are offset by -1112.85px from LandingPage coordinates */}
      <motion.section
        ref={desktopRef}
        className="hidden lg:block"
        style={{
          position: 'relative',
          width: '100%',
          height: '1548px',
          backgroundColor: 'transparent',
          overflow: 'visible',
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        {/* ── Side decorative images ── */}
        <div style={{ position: 'absolute', left: '677px', top: '13px', width: '605px', height: '350px', zIndex: 329, transform: 'rotate(89deg) scaleX(-1)', pointerEvents: 'none' }}>
          <img src="https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?w=1386&e=webp&cX=0&cY=0&cW=1438&cH=831" className="fixMobileImageStretch" alt="" style={{ opacity: 0.43 }} draggable={false} />
        </div>
        <div style={{ position: 'absolute', left: '642px', top: '596px', width: '605px', height: '350px', zIndex: 381, transform: 'rotate(-87deg) scaleX(-1)', pointerEvents: 'none' }}>
          <img src="https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?w=1386&e=webp&cX=1&cY=0&cW=1436&cH=831" className="fixMobileImageStretch" alt="" style={{ opacity: 0.43 }} draggable={false} />
        </div>
        <div style={{ position: 'absolute', left: '-241px', top: '0px', width: '590px', height: '338px', zIndex: 330, transform: 'rotate(91deg)', pointerEvents: 'none' }}>
          <img src="https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?w=1352&e=webp&cX=0&cY=5&cW=1440&cH=825" className="fixMobileImageStretch" alt="" style={{ opacity: 0.27 }} draggable={false} />
        </div>
        <div style={{ position: 'absolute', left: '-101px', top: '554px', width: '590px', height: '338px', zIndex: 382, transform: 'rotate(35deg)', pointerEvents: 'none' }}>
          <img src="https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?w=1352&e=webp&cX=0&cY=5&cW=1440&cH=825" className="fixMobileImageStretch" alt="" style={{ opacity: 0.27 }} draggable={false} />
        </div>
        <div style={{ position: 'absolute', left: '677px', top: '1179px', width: '605px', height: '350px', zIndex: 329, transform: 'rotate(89deg) scaleX(-1)', pointerEvents: 'none' }}>
          <img src="https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?w=1386&e=webp&cX=0&cY=0&cW=1438&cH=831" className="fixMobileImageStretch" alt="" style={{ opacity: 0.43 }} draggable={false} />
        </div>
        <div style={{ position: 'absolute', left: '-241px', top: '1108px', width: '590px', height: '338px', zIndex: 330, transform: 'rotate(91deg)', pointerEvents: 'none' }}>
          <img src="https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/4610144/image-0bdd0551-e329-4b7b-baf5-bbc90abfb745.webp?w=1352&e=webp&cX=0&cY=5&cW=1440&cH=825" className="fixMobileImageStretch" alt="" style={{ opacity: 0.27 }} draggable={false} />
        </div>

        {/* ── Section 1: How Playcast Brings Games to the World ── */}

        <div style={{ position: 'absolute', left: '170px', top: '76px', width: '683px', height: '323px', zIndex: 392 }}>
          <div style={{
            height: '100%',
            width: '100%',
            border: '1px solid transparent',
            background: 'linear-gradient(180deg, #3b42a0, #5d3a8a) padding-box, linear-gradient(180deg, #8fb2ff, #b58cff, #caa0ff, #7fa6ff) border-box',
            borderTopLeftRadius: '120px',
            borderTopRightRadius: '10px',
            borderBottomRightRadius: '120px',
            borderBottomLeftRadius: '10px',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }} />
        </div>

        <div style={{ position: 'absolute', left: '228px', top: '160px', width: '246px', height: '75px', zIndex: 396 }}>
          <h1 style={{ ...titleStyle, textAlign: 'left' }}>
            <span style={titleSpanStyle}>HOW PLAYCAST</span><br />
            <span style={titleSpanStyle}>BRINGS GAMES TO</span><br />
            <span style={titleSpanStyle}>THE <span style={{ ...titleSpanStyle, color: YELLOW }}>WORLD</span></span>
          </h1>
        </div>

        <div style={{ position: 'absolute', left: '228px', top: '246px', width: '227px', height: '65px', zIndex: 395 }}>
          <p style={{ ...bodyStyle, textAlign: 'left' }}>
            <span style={bodySpanStyle}>
              Game developers use Playcast technology to deliver interactive demos streamed in real time. Using our host network, players can drop into games directly — bringing PC-only experiences to any device.
            </span>
          </p>
        </div>

        {/* Decorative — top-left of card */}
        <div style={{ position: 'absolute', left: '135px', top: '39px', width: '100px', height: '104px', zIndex: 397, transform: 'rotate(-42deg)', filter: 'drop-shadow(rgba(3, 10, 42, 0.54) 1px 2px 7px)' }}>
          <img src="/home-site/section-host-away-decorative-right.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1 }} draggable={false} />
        </div>

        {/* Video */}
        <div style={{ position: 'absolute', left: '495px', top: '83px', width: '317px', height: '278px', zIndex: 394 }}>
          <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0, borderRadius: '20px', overflow: 'hidden' }}>
            <video crossOrigin="anonymous" poster="https://i.imgur.com/79a5YKm.jpg" controlsList="nodownload nofullscreen noremoteplayback noplaybackrate" disablePictureInPicture preload="auto" loop autoPlay playsInline muted style={{ display: 'block', width: '100%', height: 'auto', borderRadius: '20px', pointerEvents: 'none' }}>
              <source type="video/mp4" src="https://i.imgur.com/79a5YKm.gif" />
            </video>
          </div>
        </div>

        {/* Behind-video decorative */}
        <div style={{ position: 'absolute', left: '495px', top: '104px', width: '329px', height: '267px', zIndex: 393, filter: 'drop-shadow(rgba(65, 0, 126, 0.41) 2px 19px 12px)', backgroundImage: 'url("https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-c669ceb4-69ee-41bf-8421-10a2503b8ea0.png?w=754&e=webp&nll=true&cX=331&cY=98&cW=531&cH=431")', backgroundPosition: '50% 50%', backgroundSize: 'cover', boxShadow: 'rgba(191, 186, 255, 0.7) 0px 0px 0px 1px inset', borderRadius: '120px 10px' }} />

        {/* Decor-01 */}
        <motion.div
          style={{ position: 'absolute', left: '285px', top: '37px', width: '112px', height: '67px', zIndex: 415 }}
          animate={{ rotate: [-5, 5, -5] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/home-site/section-how-playcast-decorative-01.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1 }} draggable={false} />
        </motion.div>

        {/* Decor-02 (desktop only) */}
        <motion.div
          style={{ position: 'absolute', left: '136px', top: '130px', width: '129px', height: '236px', zIndex: 414 }}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/home-site/section-how-playcast-decorative-02.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1, transform: 'scaleX(-1)' }} draggable={false} />
        </motion.div>

        {/* Decor — top-right of card */}
        <motion.div
          style={{ position: 'absolute', left: '845px', top: '90px', width: '45px', height: '77px', zIndex: 418 }}
          animate={{ y: [0, 11, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/home-site/section-earn-rewards-decorative-02.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1, transform: 'scaleX(-1)' }} draggable={false} />
        </motion.div>

        {/* Decor-04 (desktop only) */}
        <motion.div
          style={{ position: 'absolute', left: '812px', top: '343px', width: '83px', height: '142px', zIndex: 422 }}
          animate={{ rotate: [0, 10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/home-site/section-how-playcast-decorative-04.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1, transform: 'scaleX(-1)' }} draggable={false} />
        </motion.div>

        {/* ── Section 2: Host When You're Away From Your PC ── */}

        <div style={{ position: 'absolute', left: '170px', top: '612px', width: '683px', height: '323px', zIndex: 398 }}>
          <div style={{
            height: '100%',
            width: '100%',
            border: '1px solid transparent',
            background: 'linear-gradient(180deg, #5d3a8a, #3b42a0) padding-box, linear-gradient(180deg, #8fb2ff 0%, #b58cff 40%, #caa0ff 70%, #7fa6ff 100%) border-box',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '120px',
            borderBottomRightRadius: '10px',
            borderBottomLeftRadius: '120px',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }} />
        </div>

        <div style={{ position: 'absolute', left: '580px', top: '691px', width: '219px', height: '75px', zIndex: 402 }}>
          <h1 style={{ ...titleStyle, textAlign: 'left' }}>
            <span style={titleSpanStyle}>
              HOST WHEN YOU'RE <span style={{ ...titleSpanStyle, color: YELLOW }}>AWAY</span> FROM YOUR PC
            </span>
          </h1>
        </div>

        <div style={{ position: 'absolute', left: '580px', top: '781px', width: '227px', height: '65px', zIndex: 401 }}>
          <p style={{ ...bodyStyle, textAlign: 'left' }}>
            <span style={bodySpanStyle}>
              When your PC is inactive, Playcast securely runs in a sandboxed Windows account on your machine — isolated from your files, safe from others, and automatically relinquishes control when you return.
            </span>
          </p>
        </div>

        {/* Video */}
        <div style={{ position: 'absolute', left: '235px', top: '623px', width: '277px', height: '241px', zIndex: 400, pointerEvents: 'none' }}>
          <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0, borderRadius: '10px', overflow: 'hidden', pointerEvents: 'none' }}>
            <video crossOrigin="anonymous" poster="https://i.imgur.com/EozeXjZ.jpg" controlsList="nodownload nofullscreen noremoteplayback noplaybackrate" disablePictureInPicture preload="auto" loop autoPlay playsInline muted style={{ display: 'block', width: '100%', height: 'auto', borderRadius: '10px', pointerEvents: 'none' }}>
              <source type="video/mp4" src="https://i.imgur.com/EozeXjZ.gif" />
            </video>
          </div>
        </div>

        {/* Decor-right */}
        <div style={{ position: 'absolute', left: '773px', top: '542px', width: '135px', height: '143px', zIndex: 403, transform: 'rotate(19deg)', filter: 'drop-shadow(rgba(3, 10, 42, 0.54) 1px 2px 7px)' }}>
          <img src="/home-site/section-how-playcast-decorative-left.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1 }} draggable={false} />
        </div>

        {/* Behind-text decorative */}
        <div style={{ position: 'absolute', left: '203px', top: '641px', width: '329px', height: '267px', zIndex: 399, filter: 'drop-shadow(rgba(65, 0, 126, 0.41) 9px 12px 12px)', backgroundImage: 'url("https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-4cd56917-22b2-4984-871d-6b69c43c2003.png?w=754&e=webp&nll=true&cX=331&cY=123&cW=531&cH=431")', backgroundPosition: '50% 50%', backgroundSize: 'cover', boxShadow: 'rgba(237, 228, 255, 0.67) 0px 0px 0px 1px inset', borderRadius: '10px 120px' }} />

        {/* Decor-01 (desktop only) */}
        <motion.div
          style={{ position: 'absolute', left: '107px', top: '702px', width: '96px', height: '323px', zIndex: 416 }}
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/home-site/section-host-away-decorative-01.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1 }} draggable={false} />
        </motion.div>

        {/* Decor-02 (desktop only) */}
        <motion.div
          style={{ position: 'absolute', left: '840px', top: '772px', width: '44px', height: '153px', zIndex: 423 }}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-2990fe30-8f7b-4049-aad1-6f7c843aabd7.png?w=138&e=webp&nll=true&cX=4193&cY=934&cW=457&cH=1588" className="fixMobileImageStretch" alt="" style={{ opacity: 1, width: '100%', height: '100%' }} draggable={false} />
        </motion.div>

        {/* Decor-04 (desktop only) */}
        <motion.div
          style={{ position: 'absolute', left: '662px', top: '575px', width: '112px', height: '67px', zIndex: 424 }}
          animate={{ rotate: [-5, 5, -5] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/home-site/section-host-away-decorative-04.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1, transform: 'rotate(-21deg)' }} draggable={false} />
        </motion.div>

        {/* Decor-03 */}
        <motion.div
          style={{ position: 'absolute', left: '138px', top: '878px', width: '75px', height: '140px', zIndex: 417 }}
          animate={{ rotate: [0, 10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/home-site/section-host-away-decorative-03.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1 }} draggable={false} />
        </motion.div>

        {/* ── Section 3: Earn Rewards the Longer You Host ── */}

        <div style={{ position: 'absolute', left: '171px', top: '1133px', width: '683px', height: '323px', zIndex: 404 }}>
          <div style={{
            height: '100%',
            width: '100%',
            border: '1px solid transparent',
            background: 'linear-gradient(180deg, #3b42a0, #5d3a8a) padding-box, linear-gradient(180deg, #8fb2ff, #b58cff, #caa0ff, #7fa6ff) border-box',
            borderTopLeftRadius: '120px',
            borderTopRightRadius: '10px',
            borderBottomRightRadius: '120px',
            borderBottomLeftRadius: '10px',
            overflow: 'hidden',
            boxSizing: 'border-box',
          }} />
        </div>

        <div style={{ position: 'absolute', left: '234px', top: '1224px', width: '219px', height: '75px', zIndex: 408 }}>
          <h1 style={{ ...titleStyle, textAlign: 'left' }}>
            <span style={titleSpanStyle}>
              EARN <span style={{ ...titleSpanStyle, color: YELLOW }}>REWARDS</span> THE LONGER YOU HOST
            </span>
          </h1>
        </div>

        <div style={{ position: 'absolute', left: '234px', top: '1314px', width: '227px', height: '65px', zIndex: 407 }}>
          <p style={{ ...bodyStyle, textAlign: 'left' }}>
            <span style={bodySpanStyle}>
              {section3Body}
            </span>
          </p>
        </div>

        {/* Video */}
        <div style={{ position: 'absolute', left: '520px', top: '1145px', width: '277px', height: '241px', zIndex: 406, pointerEvents: 'none' }}>
          <div style={{ position: 'relative', display: 'inline-block', lineHeight: 0, borderRadius: '10px', overflow: 'hidden', pointerEvents: 'none' }}>
            <video crossOrigin="anonymous" poster="https://i.imgur.com/Acc7o4G.jpg" controlsList="nodownload nofullscreen noremoteplayback noplaybackrate" disablePictureInPicture preload="auto" loop autoPlay playsInline muted style={{ display: 'block', width: '100%', height: 'auto', borderRadius: '10px', pointerEvents: 'none' }}>
              <source type="video/mp4" src="https://i.imgur.com/Acc7o4G.gif" />
            </video>
          </div>
        </div>

        {/* Decor-left */}
        <div style={{ position: 'absolute', left: '121px', top: '1099px', width: '125px', height: '138px', zIndex: 413, transform: 'rotate(-20deg)', filter: 'drop-shadow(rgba(3, 10, 42, 0.88) -10px 2px 7px)' }}>
          <img src="/home-site/section-earn-rewards-decorative-left.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1 }} draggable={false} />
        </div>

        {/* Behind-video decorative */}
        <div style={{ position: 'absolute', left: '498px', top: '1156px', width: '330px', height: '278px', zIndex: 405, filter: 'drop-shadow(rgba(65, 0, 126, 0.41) -3px 21px 12px)', backgroundImage: 'url("https://i-p.rmcdn.net/65bded82faa65b750f8fc6e4/5951509/image-fe64a109-7b05-4a14-82ff-3b4370452e1a.png?w=754&e=webp&nll=true&cX=333&cY=148&cW=548&cH=463")', backgroundPosition: '50% 50%', backgroundSize: 'cover', boxShadow: 'rgba(158, 150, 255, 0.7) 0px 0px 0px 1px inset', borderRadius: '120px 10px' }} />

        {/* Decor-01 */}
        <motion.div
          style={{ position: 'absolute', left: '191px', top: '1101px', width: '112px', height: '67px', zIndex: 420 }}
          animate={{ rotate: [-5, 5, -5] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/home-site/section-earn-rewards-decorative-01.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1, transform: 'scaleX(-1)' }} draggable={false} />
        </motion.div>

        {/* Decor-02 (desktop only) */}
        <motion.div
          style={{ position: 'absolute', left: '856px', top: '1281px', width: '45px', height: '77px', zIndex: 418 }}
          animate={{ y: [0, 11, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/img/aida/HostThreePlatform/z641vM-F5CdmkHxODKg00.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1 }} draggable={false} />
        </motion.div>

        {/* Decor-03 (desktop only) */}
        <motion.div
          style={{ position: 'absolute', left: '137px', top: '1184px', width: '129px', height: '236px', zIndex: 419 }}
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/home-site/section-earn-rewards-decorative-03.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1, transform: 'scaleX(-1)' }} draggable={false} />
        </motion.div>

        {/* Decor-04 (desktop only) */}
        <motion.div
          style={{ position: 'absolute', left: '811px', top: '1407px', width: '75px', height: '140px', zIndex: 425 }}
          animate={{ rotate: [0, 10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img src="/home-site/section-earn-rewards-decorative-04.png" className="fixMobileImageStretch" alt="" style={{ opacity: 1, transform: 'scaleX(-1)' }} draggable={false} />
        </motion.div>
      </motion.section>

      {/* ── Mobile Layout — stacked section cards ── */}
      <motion.section
        ref={mobileRef}
        className="flex w-full flex-col items-center lg:hidden"
        style={{ backgroundColor: 'transparent', padding: '20px 16px', gap: '32px' }}
        initial={{ opacity: 0 }}
        animate={isMobileInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Mobile: How Playcast Brings Games to the World */}
        <div style={{ position: 'relative', marginTop: '70px' }}>
          <div style={{ position: 'relative', width: '344px', height: '580px', overflow: 'visible' }}>
            {/* Background card */}
            <div style={{
              position: 'absolute', inset: 0,
              border: '1px solid transparent',
              background: 'linear-gradient(180deg, #3b42a0, #5d3a8a) padding-box, linear-gradient(180deg, #8fb2ff, #b58cff, #caa0ff, #7fa6ff) border-box',
              borderTopLeftRadius: '120px', borderTopRightRadius: '10px',
              borderBottomRightRadius: '120px', borderBottomLeftRadius: '10px',
              overflow: 'hidden', boxSizing: 'border-box',
            }} />

            {/* Gibber — floats above top edge */}
            <img src={MOB_S1_GIBBER} alt="" draggable={false} style={{
              position: 'absolute', left: '-39px', top: '-111px',
              width: '156px', height: '180px',
              transform: 'rotate(-8deg) scaleX(-1)',
              filter: 'drop-shadow(rgba(3, 10, 42, 0.54) 1px 2px 7px)',
            }} />

            {/* Decor — floats above top-right, animated */}
            <motion.div
              style={{ position: 'absolute', left: '203px', top: '-46px', width: '140px', height: '84px', overflow: 'visible' }}
              animate={{ rotate: [-5, 5, -5] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            >
              <img src={MOB_S1_DECOR} alt="" draggable={false} style={{ width: '100%', height: '100%' }} />
            </motion.div>

            {/* Title */}
            <div style={{ position: 'absolute', left: '30px', top: '41px', width: '285px' }}>
              <h2 style={{ lineHeight: '32px', fontFamily: 'Inter', fontWeight: 900, fontSize: '28px', textAlign: 'right', color: WHITE, margin: 0, fontVariationSettings: "'wght' 900" }}>
                HOW PLAYCAST<br />BRINGS GAMES TO<br />THE <span style={{ color: YELLOW }}>WORLD</span>
              </h2>
            </div>

            {/* Body */}
            <div style={{ position: 'absolute', left: '23px', top: '155px', width: '295px' }}>
              <p style={{ lineHeight: '22px', fontFamily: 'Inter', fontWeight: 400, fontSize: '16px', textAlign: 'right', color: WHITE, margin: 0 }}>
                Game developers use Playcast technology to deliver interactive demos streamed in real time. Using our host network, players can drop into games directly — bringing PC-only experiences to any device.
              </p>
            </div>

            {/* Inner card background */}
            <div style={{
              position: 'absolute', left: '23px', top: '308px', width: '300px', height: '255px',
              transform: 'rotate(-180deg)',
              filter: 'drop-shadow(rgba(65, 0, 126, 0.41) 2px 19px 12px)',
              backgroundImage: `url("${MOB_S1_INNER_CARD}")`,
              backgroundPosition: '50% 50%', backgroundSize: 'cover',
              boxShadow: 'rgba(191, 186, 255, 0.7) 0px 0px 0px 1px inset',
              borderRadius: '120px 10px',
            }} />

            {/* Video */}
            <div style={{ position: 'absolute', left: '34px', top: '301px', width: '283px', height: '285px', borderRadius: '20px', overflow: 'hidden' }}>
              <img src="https://i.imgur.com/79a5YKm.gif" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '20px' }} />
            </div>
          </div>
        </div>

        {/* Mobile: Host When You're Away From Your PC */}
        <div style={{ position: 'relative', marginTop: '65px' }}>
          <div style={{ position: 'relative', width: '344px', height: '625px', overflow: 'visible' }}>
            {/* Background card — flipped gradient, flipped radii */}
            <div style={{
              position: 'absolute', inset: 0,
              border: '1px solid transparent',
              background: 'linear-gradient(180deg, #5d3a8a, #3b42a0) padding-box, linear-gradient(180deg, #8fb2ff 0%, #b58cff 40%, #caa0ff 70%, #7fa6ff 100%) border-box',
              borderTopLeftRadius: '10px', borderTopRightRadius: '120px',
              borderBottomRightRadius: '10px', borderBottomLeftRadius: '120px',
              overflow: 'hidden', boxSizing: 'border-box',
            }} />

            {/* Gibber — floats above top-right */}
            <img src={MOB_S2_GIBBER} alt="" draggable={false} style={{
              position: 'absolute', left: '232px', top: '-76px',
              width: '126px', height: '151px',
              transform: 'rotate(31deg)',
              filter: 'drop-shadow(rgba(3, 10, 42, 0.54) 1px 2px 7px)',
            }} />

            {/* Title */}
            <div style={{ position: 'absolute', left: '29px', top: '42px', width: '241px' }}>
              <h2 style={{ lineHeight: '32px', fontFamily: 'Inter', fontWeight: 900, fontSize: '28px', textAlign: 'left', color: WHITE, margin: 0, fontVariationSettings: "'wght' 900" }}>
                HOST WHEN YOU'RE <span style={{ color: YELLOW }}>AWAY</span> FROM YOUR PC
              </h2>
            </div>

            {/* Body */}
            <div style={{ position: 'absolute', left: '31px', top: '155px', width: '273px' }}>
              <p style={{ lineHeight: '22px', fontFamily: 'Inter', fontWeight: 400, fontSize: '16px', textAlign: 'left', color: WHITE, margin: 0 }}>
                When your PC is inactive, Playcast securely runs in a sandboxed Windows account on your machine — isolated from your files, safe from others, and automatically relinquishes control when you return.
              </p>
            </div>

            {/* Inner card background */}
            <div style={{
              position: 'absolute', left: '25px', top: '311px', width: '295px', height: '290px',
              transform: 'rotate(-180deg)',
              filter: 'drop-shadow(rgba(65, 0, 126, 0.41) 9px 12px 12px)',
              backgroundImage: `url("${MOB_S2_INNER_CARD}")`,
              backgroundPosition: '50% 50%', backgroundSize: 'cover',
              boxShadow: 'rgba(237, 228, 255, 0.67) 0px 0px 0px 1px inset',
              borderRadius: '10px 120px',
            }} />

            {/* Video */}
            <div style={{ position: 'absolute', left: '54px', top: '318px', width: '250px', height: '266px', borderRadius: '10px', overflow: 'hidden' }}>
              <img src="https://i.imgur.com/EozeXjZ.gif" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '10px' }} />
            </div>
          </div>
        </div>

        {/* Mobile: Earn Rewards the Longer You Host */}
        <div style={{ position: 'relative', marginTop: '65px' }}>
          <div style={{ position: 'relative', width: '344px', height: '580px', overflow: 'visible' }}>
            {/* Background card — same gradient + radii as card 1 */}
            <div style={{
              position: 'absolute', inset: 0,
              border: '1px solid transparent',
              background: 'linear-gradient(180deg, #3b42a0, #5d3a8a) padding-box, linear-gradient(180deg, #8fb2ff, #b58cff, #caa0ff, #7fa6ff) border-box',
              borderTopLeftRadius: '120px', borderTopRightRadius: '10px',
              borderBottomRightRadius: '120px', borderBottomLeftRadius: '10px',
              overflow: 'hidden', boxSizing: 'border-box',
            }} />

            {/* Gibber — floats above top-left */}
            <img src={MOB_S3_GIBBER} alt="" draggable={false} style={{
              position: 'absolute', left: '-22px', top: '-82px',
              width: '128px', height: '154px',
              transform: 'rotate(-2deg)',
              filter: 'drop-shadow(rgba(1, 15, 81, 0.45) -10px 2px 7px)',
            }} />

            {/* Decor — floats above top-right, animated */}
            <motion.div
              style={{ position: 'absolute', left: '208px', top: '-76px', width: '161px', height: '128px', overflow: 'visible' }}
              animate={{ rotate: [-5, 5, -5] }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
            >
              <img src={MOB_S3_DECOR} alt="" draggable={false} style={{
                position: 'absolute', left: '10px', top: '22px',
                width: '140px', height: '84px',
                transform: 'rotate(-21deg)',
              }} />
            </motion.div>

            {/* Title */}
            <div style={{ position: 'absolute', left: '67px', top: '38px', width: '248px' }}>
              <h2 style={{ lineHeight: '32px', fontFamily: 'Inter', fontWeight: 900, fontSize: '28px', textAlign: 'right', color: WHITE, margin: 0, fontVariationSettings: "'wght' 900" }}>
                EARN <span style={{ color: YELLOW }}>REWARDS</span> THE LONGER YOU HOST
              </h2>
            </div>

            {/* Body */}
            <div style={{ position: 'absolute', left: '44px', top: '152px', width: '273px' }}>
              <p style={{ lineHeight: '22px', fontFamily: 'Inter', fontWeight: 400, fontSize: '16px', textAlign: 'right', color: WHITE, margin: 0 }}>
                {section3Body}
              </p>
            </div>

            {/* Inner card background */}
            <div style={{
              position: 'absolute', left: '23px', top: '289px', width: '298px', height: '275px',
              transform: 'rotate(-180deg)',
              filter: 'drop-shadow(rgba(65, 0, 126, 0.41) -3px 21px 12px)',
              backgroundImage: `url("${MOB_S3_INNER_CARD}")`,
              backgroundPosition: '50% 50%', backgroundSize: 'cover',
              boxShadow: 'rgba(158, 150, 255, 0.7) 0px 0px 0px 1px inset',
              borderRadius: '120px 10px',
            }} />

            {/* Video */}
            <div style={{ position: 'absolute', left: '25px', top: '296px', width: '273px', height: '259px', borderRadius: '10px', overflow: 'hidden' }}>
              <img src="https://i.imgur.com/Acc7o4G.gif" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '10px' }} />
            </div>
          </div>
        </div>
      </motion.section>
    </>
  );
};
